import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query'
import {
  ChevronDown,
  Clover,
  LogOut,
  PanelsRightBottom,
  Trash2,
  User,
} from 'lucide-react'
import { destroyCookie, parseCookies } from 'nookies'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import * as z from 'zod'

import { deleteUser } from '@/api/user/delete-user'
import { getProfile } from '@/api/user/get-profile'
import { signOut } from '@/api/user/sign-out'
import { updateProfile, type UpdateProfileBody } from '@/api/user/updateProfile'

import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Skeleton } from './ui/skeleton'

const profileFormSchema = z.object({
  nome: z
    .string()
    .min(1, 'Nome obrigatório')
    .regex(
      /^[a-zA-ZÀ-ú]{2,}( [a-zA-ZÀ-ú]{2,})+$/,
      'Digite pelo menos nome e sobrenome',
    ),
  whatsapp: z
    .string()
    .min(14, 'Whatsapp inválido')
    .max(15, 'Whatsapp inválido')
    .regex(/^\(\d{2}\) \d{4,5}-\d{4}$/, 'Whatsapp inválido'),
})

type ProfileFormData = z.infer<typeof profileFormSchema>

export function AccountMenu() {
  const navigate = useNavigate()
  const cookies = parseCookies()
  const { 'reactAuth.email': email, 'reactAuth.id': userId } = cookies

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['profile', email],
    queryFn: () => getProfile({ email }),
    staleTime: Infinity,
  })

  const {
    mutateAsync: updateProfileFn,
  }: UseMutationResult<unknown, Error, UpdateProfileBody, unknown> =
    useMutation({
      mutationFn: updateProfile,
      onSuccess: () => {
        toast.success('Perfil atualizado com sucesso!')
        window.location.reload()
      },
      onError: () => {
        toast.error('Erro ao atualizar perfil')
      },
    })

  const { mutateAsync: signOutFn, isPending: isSigningOut } = useMutation({
    mutationFn: signOut,
    onSuccess: () => {
      navigate('/sign-in', { replace: true })
    },
  })

  const { mutateAsync: deleteUserFn, isPending: isDeletingAccount } =
    useMutation({
      mutationFn: deleteUser,
      onSuccess: () => {
        toast.success('Conta excluída com sucesso')
        destroyCookie(undefined, 'reactAuth.token')
        destroyCookie(undefined, 'reactAuth.refreshToken')
        destroyCookie(undefined, 'reactAuth.email')
        destroyCookie(undefined, 'reactAuth.id')
        navigate('/sign-in', { replace: true })
      },
      onError: () => {
        toast.error('Erro ao excluir conta')
      },
    })

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      nome: '',
      whatsapp: '',
    },
  })

  useEffect(() => {
    const formatPhone = (phone: string) => {
      const onlyDigits = phone.replace(/\D/g, '')
      if (onlyDigits.length <= 10) {
        return onlyDigits
          .replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3')
          .trim()
      } else {
        return onlyDigits
          .replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3')
          .trim()
      }
    }

    if (profile) {
      form.reset({
        nome: profile.nome || '',
        whatsapp: formatPhone(profile.whatsapp || ''),
      })
    }
  }, [profile, form])

  const onSubmit = async (data: ProfileFormData) => {
    if (!profile?.id || !data.nome || !data.whatsapp) {
      toast.error('Nome e telefone são obrigatórios')
      return
    }

    const updatedData: UpdateProfileBody = {
      id: profile.id,
      nome: data.nome,
      email,
      whatsapp: data.whatsapp,
    }

    await updateProfileFn(updatedData)
  }

  const handleSignOut = async () => {
    const { 'reactAuth.refreshToken': refreshToken } = parseCookies()

    try {
      if (refreshToken) await signOutFn(refreshToken)
      destroyCookie(undefined, 'reactAuth.token')
      destroyCookie(undefined, 'reactAuth.refreshToken')
      destroyCookie(undefined, 'reactAuth.email')
      destroyCookie(undefined, 'reactAuth.id')
    } catch (err) {
      console.error('Erro ao fazer logout:', err)
    } finally {
      window.location.href = '/sign-in'
    }
  }

  const confirmDeleteUser = async () => {
    try {
      await deleteUserFn(Number(userId))
    } catch (error) {
      console.error('Erro ao excluir conta:', error)
    } finally {
      setConfirmDialogOpen(false)
    }
  }

  return (
    <>
      <Dialog>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="flex items-center gap-2 select-none"
            >
              {isLoadingProfile ? (
                <Skeleton className="h-4 w-20" />
              ) : (
                profile?.nome
              )}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex flex-col gap-1">
              {isLoadingProfile ? (
                <>
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </>
              ) : (
                <>
                  <span>{profile?.nome}</span>
                  <span className="text-muted-foreground text-xs font-normal">
                    {profile?.email}
                  </span>
                </>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DialogTrigger asChild>
              <DropdownMenuItem>
                <User className="mr-2 h-4 w-4" />
                <span>Perfil completo</span>
              </DropdownMenuItem>
            </DialogTrigger>

            {profile?.role === 'Admin' && (
              <DropdownMenuItem onClick={() => navigate('/campanhas/minhas')}>
                <Clover className="mr-2 h-4 w-4" />
                <span>Minhas campanhas</span>
              </DropdownMenuItem>
            )}

            {profile?.role === 'Admin' && (
              <DropdownMenuItem onClick={() => navigate('/admin')}>
                <PanelsRightBottom className="mr-2 h-4 w-4" />
                <span>Painel Admin</span>
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              onClick={() => setConfirmDialogOpen(true)}
              disabled={isDeletingAccount}
              className="text-rose-500 dark:text-rose-400"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              <span>Excluir conta</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              asChild
              disabled={isSigningOut}
              className="text-rose-500 dark:text-rose-400"
            >
              <button className="w-full" onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sair</span>
              </button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DialogContent>
          <DialogHeader>
            <DialogTitle>Perfil</DialogTitle>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome completo</Label>
              <Input
                id="nome"
                {...form.register('nome')}
                placeholder="Nome e sobrenome"
              />
              {form.formState.errors.nome && (
                <p className="text-sm text-red-500">
                  {form.formState.errors.nome.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={profile?.email || ''}
                disabled
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp">WhatsApp</Label>
              <Input
                id="whatsapp"
                maxLength={15}
                {...form.register('whatsapp', {
                  onChange: (e) => {
                    const input = e.target.value
                    const onlyDigits = input.replace(/\D/g, '').slice(0, 11) // Limita a 11 dígitos numéricos
                    let formatted = ''
                    if (onlyDigits.length <= 10) {
                      formatted = onlyDigits
                        .replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3')
                        .trim()
                    } else {
                      formatted = onlyDigits
                        .replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3')
                        .trim()
                    }
                    e.target.value = formatted
                  },
                })}
                placeholder="(00) 00000-0000"
              />

              {form.formState.errors.whatsapp && (
                <p className="text-sm text-red-500">
                  {form.formState.errors.whatsapp.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={!form.formState.isDirty}
            >
              Salvar
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmação de exclusão */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            Tem certeza que deseja excluir sua conta? Esta ação não poderá ser
            desfeita.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
              disabled={isDeletingAccount}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteUser}
              disabled={isDeletingAccount}
            >
              {isDeletingAccount ? 'Excluindo...' : 'Confirmar exclusão'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
